import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";
import { vendorAPI } from "../../services";
import { useSelector } from "react-redux";

const BatchForm = ({ onSubmit, onCancel, editingBatch, suppliers }) => {
  const [formData, setFormData] = useState({
    amount: "",
    totalQuantity: "",
    invoiceNumber: "",
    description: "",
    supplierId: "",
  });

  const [errors, setErrors] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [supplierOptions, setSupplierOptions] = useState(suppliers || []);
  const searchInputRef = useRef(null);
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  // Sync options if parent provides or updates suppliers
  useEffect(() => {
    if (Array.isArray(suppliers) && suppliers.length > 0) {
      setSupplierOptions(suppliers);
    }
  }, [suppliers]);

  useEffect(() => {
    if (editingBatch) {
      setFormData({
        amount: editingBatch.amount || "",
        totalQuantity: editingBatch.totalQuantity || "",
        invoiceNumber: editingBatch.invoiceNumber || "",
        description: editingBatch.description || "",
        supplierId: editingBatch.supplierId || "",
      });
    }
  }, [editingBatch]);

  // Load suppliers on form open (when Add Batch is clicked)
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await vendorAPI.getVendorsList();
        const list = Array.isArray(response?.results)
          ? response.results
          : Array.isArray(response)
          ? response
          : [];
        if (list.length > 0) setSupplierOptions(list);
      } catch (err) {
        console.warn("BatchForm: Failed to fetch suppliers on open", err);
      }
    };
    fetchSuppliers();
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Debounced search API call
  useEffect(() => {
    if (!searchTerm) return;

    const timer = setTimeout(async () => {
      try {
        const response = await vendorAPI.getVendorsList(searchTerm);
        const list = Array.isArray(response?.results)
          ? response.results
          : Array.isArray(response)
          ? response
          : [];
        setSupplierOptions(list);
      } catch (err) {
        console.warn("BatchForm: Failed to search suppliers", err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const validateForm = () => {
    const newErrors = {};

    // Only validate supplier as required
    if (!formData.supplierId) {
      newErrors.supplierId = "Supplier is required";
    }

    // Optional validation for amount (only if provided)
    if (formData.amount && parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    // Optional validation for total quantity (only if provided)
    if (formData.totalQuantity && parseInt(formData.totalQuantity) <= 0) {
      newErrors.totalQuantity = "Total quantity must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const batchData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        totalQuantity: formData.totalQuantity
          ? parseInt(formData.totalQuantity)
          : 0,
        supplierId: formData.supplierId,
      };

      if (editingBatch) {
        batchData.id = editingBatch.id;
        batchData.updatedAt = new Date().toISOString();
      } else {
        batchData.id = Date.now().toString();
        batchData.createdAt = new Date().toISOString();
      }

      onSubmit(batchData);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    setErrors((prev) => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const handleSupplierSelect = (supplierId) => {
    setFormData((prev) => ({
      ...prev,
      supplierId: supplierId,
    }));
    setIsDropdownOpen(false);
    setSearchTerm("");

    // Clear error when user selects a supplier
    setErrors((prev) => {
      if (prev.supplierId) {
        const newErrors = { ...prev };
        delete newErrors.supplierId;
        return newErrors;
      }
      return prev;
    });
  };

  const filteredSuppliers = supplierOptions;

  const selectedSupplier = supplierOptions.find(
    (s) => s.id === formData.supplierId
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        !event.target.closest(".batch-supplier-dropdown-container")
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="add-purchase-modal-overlay">
      <div className="add-purchase-modal noscrollbar">
        <div className="add-purchase-modal-header">
          <h2 className="add-purchase-modal-title">
            {editingBatch ? "Edit Batch" : "Add New Batch"}
          </h2>
          <p className="add-purchase-modal-subtitle">
            {editingBatch
              ? "Update batch information"
              : "Create a new inventory batch"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="add-purchase-modal-form">
          <div className="add-purchase-modal-body">
            <div className="batches-form-group">
              <label htmlFor="supplierId" className="batches-form-label">
                Supplier *
              </label>
              <div className="batch-supplier-dropdown-container">
                <div
                  className={`${
                    isPartsURL
                      ? "dropdown-input-parts"
                      : isAccessoriesURL
                      ? "dropdown-input-acc"
                      : isGodownURL
                      ? "dropdown-input-godown"
                      : "dropdown-input"
                  } ${errors.supplierId ? "is-invalid" : ""}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span
                    className={
                      selectedSupplier ? "selected-value" : "not-selected-value"
                    }
                  >
                    {selectedSupplier
                      ? selectedSupplier.name
                      : "Select supplier"}
                  </span>
                  <ChevronDown className="dropdown-arrow" size={16} />
                </div>

                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-search">
                      <Search className="batches-search-icon" size={16} />
                      <input
                        ref={searchInputRef}
                        type="text"
                        className={`${
                          isPartsURL
                            ? "batches-search-input-parts"
                            : isAccessoriesURL
                            ? "batches-search-input-acc"
                            : isGodownURL
                            ? "batches-search-input-godown"
                            : "batches-search-input"
                        }`}
                        placeholder="Search supplier name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="dropdown-list noscrollbar">
                      {filteredSuppliers.map((supplier) => (
                        <div
                          key={supplier.id}
                          className="batches-dropdown-item"
                          onClick={() => handleSupplierSelect(supplier.id)}
                        >
                          {supplier.name}
                        </div>
                      ))}
                      {filteredSuppliers.length === 0 && (
                        <div className="batches-dropdown-item no-results">
                          No suppliers found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.supplierId && (
                <div className="batches-invalid-feedback">
                  {errors.supplierId}
                </div>
              )}
            </div>

            <div className="batches-form-group">
              <label htmlFor="invoiceNumber" className="batches-form-label">
                Invoice No.
              </label>
              <input
                type="text"
                className={`${
                  isPartsURL
                    ? "batches-form-input-parts"
                    : isAccessoriesURL
                    ? "batches-form-input-acc"
                    : isGodownURL
                    ? "batches-form-input-godown"
                    : "batches-form-input"
                } ${errors.invoiceNumber ? "is-invalid" : ""}`}
                id="invoiceNumber"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={handleChange}
                placeholder="e.g. INV-2024-001"
              />
              {errors.invoiceNumber && (
                <div className="batches-invalid-feedback">
                  {errors.invoiceNumber}
                </div>
              )}
            </div>

            <div className="batches-form-row">
              <div className="batches-form-group">
                <label htmlFor="amount" className="batches-form-label">
                  Amount
                </label>
                <input
                  type="number"
                  className={`${
                    isPartsURL
                      ? "batches-form-input-parts"
                      : isAccessoriesURL
                      ? "batches-form-input-acc"
                      : isGodownURL
                      ? "batches-form-input-godown"
                      : "batches-form-input"
                  } ${errors.amount ? "is-invalid" : ""}`}
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0.0"
                />
                {errors.amount && (
                  <div className="batches-invalid-feedback">
                    {errors.amount}
                  </div>
                )}
              </div>

              <div className="batches-form-group">
                <label htmlFor="totalQuantity" className="batches-form-label">
                  Total Qty.
                </label>
                <input
                  type="number"
                  className={`${
                    isPartsURL
                      ? "batches-form-input-parts"
                      : isAccessoriesURL
                      ? "batches-form-input-acc"
                      : isGodownURL
                      ? "batches-form-input-godown"
                      : "batches-form-input"
                  } ${errors.totalQuantity ? "is-invalid" : ""}`}
                  id="totalQuantity"
                  name="totalQuantity"
                  value={formData.totalQuantity}
                  onChange={handleChange}
                  min="1"
                  placeholder="0"
                />
                {errors.totalQuantity && (
                  <div className="batches-invalid-feedback">
                    {errors.totalQuantity}
                  </div>
                )}
              </div>
            </div>

            <div className="batches-form-group">
              <label htmlFor="description" className="batches-form-label">
                Description
              </label>
              <textarea
                className={`${
                  isPartsURL
                    ? "batches-form-input-parts"
                    : isAccessoriesURL
                    ? "batches-form-input-acc"
                    : isGodownURL
                    ? "batches-form-input-godown"
                    : "batches-form-input"
                }`}
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Mobile phone and smartphone"
              ></textarea>
            </div>
          </div>

          <div
            className="add-purchase-modal-footer"
            style={{ marginTop: "-15px" }}
          >
            <button
              type="button"
              className="brand-cancel-btn"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${
                isPartsURL
                  ? "brand-submit-btn-parts"
                  : isAccessoriesURL
                  ? "brand-submit-btn-acc"
                  : isGodownURL
                  ? "brand-submit-btn-godown"
                  : "brand-submit-btn"
              }`}
            >
              {editingBatch ? "Update Batch" : "Add Batch"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchForm;
